import { useState, useEffect } from "react";
import SettingsTabsNavigation from "../../../components/Tabs/SettingsTabsNavigation";
import DateRangePicker from "react-bootstrap-daterangepicker";
import "bootstrap-daterangepicker/daterangepicker.css";
import { Modal } from "react-bootstrap";
import {
  AuditLogService,
  AuditLogEntry,
  AuditLogTimelineResponse,
  ActionType,
} from "./auditLog.service";
import TablePagination from "../../../components/pagination/TablePagination";
import { getLanguageLabel } from "../../../components/language";
import { formatCustomDateTime } from "../../../utils/dateUtils";

interface FieldChange {
  field: string;
  oldValue: string | null;
  newValue: string | null;
}

const AuditLog = () => {
  const formatFieldValue = (
    fieldName: string,
    value: string | null
  ): string => {
    if (!value) return "(empty)";

    const isDateField = /\b(date|datetime|time)\b/i.test(fieldName);

    if (isDateField) {
      const isDateOnly =
        /\bdate\b/i.test(fieldName) && !/\b(datetime|time)\b/i.test(fieldName);

      try {
        const formatted = formatCustomDateTime(value, isDateOnly);

        if (formatted !== "Invalid date" && formatted !== "Not specified") {
          return formatted;
        }
      } catch (error) {
        console.warn(`Failed to format date field ${fieldName}:`, error);
      }
    }

    return value;
  };
  const [actionTypeFilter, setActionTypeFilter] = useState<number | null>(null);
  const [actionSourceFilter, setActionSourceFilter] = useState<string>("All");
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    null,
    null,
  ]);
  const [selectedRangeLabel, setSelectedRangeLabel] =
    useState<string>("Choose Date");
  const [selectedEntry, setSelectedEntry] = useState<AuditLogEntry | null>(
    null
  );
  const [showTimelineModal, setShowTimelineModal] = useState<boolean>(false);
  const [showDiffModal, setShowDiffModal] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [auditEntries, setAuditEntries] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [timelineData, setTimelineData] =
    useState<AuditLogTimelineResponse | null>(null);
  const [loadingTimeline, setLoadingTimeline] = useState<boolean>(false);
  const [actionTypes, setActionTypes] = useState<ActionType[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const pageSize = 15;

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    const fetchActionTypes = async () => {
      try {
        const response = await AuditLogService.getActionTypes();
        setActionTypes(response.results || []);
      } catch (error) {
        console.error("Error fetching action types:", error);
      }
    };

    fetchActionTypes();
  }, []);

  useEffect(() => {
    const fetchAuditLogs = async () => {
      setLoading(true);
      try {
        const params: any = {
          page: currentPage,
          page_size: pageSize,
        };

        if (actionTypeFilter !== null) {
          params.action_type_id = actionTypeFilter;
        }

        if (actionSourceFilter !== "All") {
          params.action_source_type = actionSourceFilter.toLowerCase();
        }

        if (dateRange[0]) {
          params.created_datetime_gte = dateRange[0]
            .toISOString()
            .split("T")[0];
        }
        if (dateRange[1]) {
          params.created_datetime_lte = dateRange[1]
            .toISOString()
            .split("T")[0];
        }

        const response = await AuditLogService.getAuditLogs(params);
        setAuditEntries(response.results || []);
        setTotalCount(response.count || 0);
      } catch (error) {
        console.error("Error fetching audit logs:", error);
        setAuditEntries([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    };

    fetchAuditLogs();
  }, [actionTypeFilter, actionSourceFilter, dateRange, currentPage]);

  const initialDateRangeSettings = {
    endDate: new Date(),
    ranges: {
      "Last 30 Days": [
        new Date(new Date().setDate(new Date().getDate() - 30)),
        new Date(),
      ],
      "Last 7 Days": [
        new Date(new Date().setDate(new Date().getDate() - 7)),
        new Date(),
      ],
      "This Month": [
        new Date(new Date().getFullYear(), new Date().getMonth(), 1),
        new Date(),
      ],
      Today: [new Date(), new Date()],
    },
    startDate: new Date(new Date().setDate(new Date().getDate() - 30)),
    timePicker: false,
  };

  const parseFieldChanges = (
    oldValueStr: string | null,
    newValueStr: string | null
  ): FieldChange[] => {
    const changes: FieldChange[] = [];

    if (!oldValueStr || !newValueStr) {
      return changes;
    }

    try {
      const oldValue = JSON.parse(oldValueStr);
      const newValue = JSON.parse(newValueStr);

      const allKeys = new Set([
        ...Object.keys(oldValue),
        ...Object.keys(newValue),
      ]);

      allKeys.forEach((key) => {
        if (key === "updated_datetime") {
          return;
        }

        const oldVal = oldValue[key];
        const newVal = newValue[key];

        if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
          const fieldLabel = key
            .split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");

          changes.push({
            field: fieldLabel,
            oldValue:
              oldVal !== null && oldVal !== undefined ? String(oldVal) : null,
            newValue:
              newVal !== null && newVal !== undefined ? String(newVal) : null,
          });
        }
      });

      return changes;
    } catch (error) {
      console.error("Error parsing field changes:", error);
      return changes;
    }
  };

  const formatTimestamp = (dateString: string): string => {
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = {
      month: "short",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    };
    return date.toLocaleString("en-US", options);
  };

  const getRecordLabel = (entry: AuditLogEntry): string => {
    if (entry.crm_related_entity.title) {
      return `${entry.crm_related_entity.display_name} - ${entry.crm_related_entity.title}`;
    } else if (entry.crm_related_entity.related_entity_id) {
      return `${entry.crm_related_entity.display_name} #${entry.crm_related_entity.related_entity_id}`;
    } else {
      return entry.crm_related_entity.display_name;
    }
  };

  const getEntityName = (entry: AuditLogEntry): string => {
    return (
      entry.crm_related_entity.title || entry.crm_related_entity.display_name
    );
  };

  const getDescription = (entry: AuditLogEntry): string => {
    if (entry.action_type.key === "LOGIN") {
      return "Logged in to the portal.";
    }

    const action = entry.action_type.display_name;
    const record = entry.crm_related_entity.display_name;
    const source = entry.action_by_api_client
      ? `via ${entry.action_by_api_client.client_name}`
      : `via ${entry.action_source_type}`;

    return `${action} ${record} ${source}.`;
  };

  const filteredAuditEntries = auditEntries;

  const handleDateRangeChange = (event: any, picker: any) => {
    const start = picker.startDate?.toDate() || null;
    const end = picker.endDate?.toDate() || null;
    const label = picker.chosenLabel || "Custom Range";
    setDateRange([start, end]);
    setSelectedRangeLabel(label);
  };

  const handleDateRangeCancel = () => {
    setDateRange([null, null]);
    setSelectedRangeLabel("Choose Date");
  };

  const handleResetFilters = () => {
    setActionTypeFilter(null);
    setActionSourceFilter("All");
    setDateRange([null, null]);
    setSelectedRangeLabel("Choose Date");
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleShowTimeline = async () => {
    if (!selectedEntry) {
      return;
    }

    setLoadingTimeline(true);
    setShowTimelineModal(true);

    try {
      const timeline = await AuditLogService.getAuditLogTimeline(
        selectedEntry.crm_audit_log_id
      );
      setTimelineData(timeline);
    } catch (error) {
      console.error("Error fetching timeline:", error);
      setTimelineData(null);
    } finally {
      setLoadingTimeline(false);
    }
  };

  const handleEntryClick = (entry: AuditLogEntry) => {
    if (entry.action_type.key === "LOGIN") {
      return;
    }

    if (isMobile) {
      setSelectedEntry(entry);
      setShowDiffModal(true);
    } else {
      if (selectedEntry?.crm_audit_log_id === entry.crm_audit_log_id) {
        setSelectedEntry(null);
      } else {
        setSelectedEntry(entry);
      }
    }
  };

  const handleCloseTimelineModal = (e?: any) => {
    setShowTimelineModal(false);
  };

  const handleCloseDiffModal = (e?: any) => {
    setShowDiffModal(false);
    setSelectedEntry(null);
  };

  return (
    <>
      <style>
        {`
          .custom-modal-dialog {
            margin: 0 auto !important;
            max-width: 100% !important;
          }
          
          @media (max-width: 991.98px) {
            .custom-modal-dialog.modal-fullscreen-lg-down {
              margin: 0 !important;
              max-width: 100% !important;
              width: 100% !important;
            }
            
            .modal.show .modal-dialog.custom-modal-dialog {
              transform: none !important;
            }
          }
          
          @media (min-width: 992px) {
            .custom-modal-dialog {
              max-width: 800px !important;
              margin: 1.75rem auto !important;
            }
          }
          
          .modal-content {
            border-radius: 8px;
            overflow: hidden;
          }

          .audit-card-hoverable:hover {
            transform: translateY(-4px);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          }

          .modal-close-btn {
            background-color: #f3f4f6 !important;
            color: #374151 !important;
          }

          .modal-close-btn:hover {
            background-color: #e5e7eb !important;
            color: #000 !important;
          }
        `}
      </style>
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              <div className="page-header">
                <div className="row align-items-center">
                  <div className="col-8">
                    <h4 className="page-title">Settings</h4>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-body pb-0 pt-2">
                  <SettingsTabsNavigation
                    activeTab="audit-log"
                    variant="bottom"
                    includeIcons={true}
                    context="organization"
                  />
                </div>
              </div>

              <div className="card">
                <div className="card-body" style={{ padding: "30px" }}>
                  <div className="mb-4">
                    <div className="d-flex gap-3 align-items-end flex-wrap">
                      <div style={{ flex: "1 1 200px", minWidth: "200px" }}>
                        <label
                          style={{
                            fontSize: "14px",
                            fontWeight: 500,
                            marginBottom: "8px",
                            display: "block",
                          }}
                        >
                          Action Type
                        </label>
                        <select
                          className="form-select"
                          value={
                            actionTypeFilter === null ? "" : actionTypeFilter
                          }
                          onChange={(e) => {
                            setActionTypeFilter(
                              e.target.value === ""
                                ? null
                                : Number(e.target.value)
                            );
                            setCurrentPage(1);
                          }}
                          style={{ fontSize: "14px" }}
                        >
                          <option value="">All</option>
                          {actionTypes.map((type) => (
                            <option
                              key={type.action_type_id}
                              value={type.action_type_id}
                            >
                              {type.action_type_display_name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div style={{ flex: "1 1 200px", minWidth: "200px" }}>
                        <label
                          style={{
                            fontSize: "14px",
                            fontWeight: 500,
                            marginBottom: "8px",
                            display: "block",
                          }}
                        >
                          Action Source
                        </label>
                        <select
                          className="form-select"
                          value={actionSourceFilter}
                          onChange={(e) =>
                            setActionSourceFilter(e.target.value)
                          }
                          style={{ fontSize: "14px" }}
                        >
                          <option>All</option>
                          <option>API Client</option>
                          <option>Portal</option>
                        </select>
                      </div>
                      <div style={{ flex: "1 1 250px", minWidth: "250px" }}>
                        <label
                          style={{
                            fontSize: "14px",
                            fontWeight: 500,
                            marginBottom: "8px",
                            display: "block",
                          }}
                        >
                          Action Time
                        </label>
                        <DateRangePicker
                          initialSettings={initialDateRangeSettings}
                          onApply={handleDateRangeChange}
                          onCancel={handleDateRangeCancel}
                        >
                          <input
                            type="text"
                            className="form-control"
                            value={selectedRangeLabel}
                            readOnly
                            style={{ fontSize: "14px", cursor: "pointer" }}
                          />
                        </DateRangePicker>
                      </div>
                      <div>
                        <button
                          className="btn btn-primary"
                          onClick={handleResetFilters}
                          style={{ fontSize: "14px" }}
                        >
                          <i className="ti ti-refresh me-1"></i>
                          Reset
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div
                      className={
                        selectedEntry && !isMobile ? "col-md-6" : "col-md-12"
                      }
                    >
                      <div className="audit-section">
                        <div className="d-flex align-items-center justify-content-between mb-4">
                          <h4
                            className="mb-0"
                            style={{ fontSize: "18px", fontWeight: 600 }}
                          >
                            Audit Log - Activity Stream
                          </h4>
                          <span className="badge badge-soft-info">
                            {totalCount}{" "}
                            {totalCount === 1 ? "entry" : "entries"}
                          </span>
                        </div>

                        <div>
                          {loading ? (
                            <div className="text-center py-5">
                              <div
                                className="spinner-border text-primary"
                                role="status"
                              >
                                <span className="visually-hidden">
                                  Loading...
                                </span>
                              </div>
                            </div>
                          ) : filteredAuditEntries.length === 0 ? (
                            <div className="text-center py-5">
                              <i
                                className="ti ti-file-search"
                                style={{ fontSize: "48px", color: "#9ca3af" }}
                              ></i>
                              <p className="mt-3" style={{ color: "#6b7280" }}>
                                No audit logs found matching the selected
                                filters.
                              </p>
                            </div>
                          ) : (
                            <>
                              {filteredAuditEntries.map((entry) => (
                                <div
                                  key={entry.crm_audit_log_id}
                                  onClick={() => handleEntryClick(entry)}
                                  className={
                                    entry.action_type.key !== "LOGIN" &&
                                      entry.action_type.key !== "SIGNUP"
                                      ? "audit-card-hoverable"
                                      : ""
                                  }
                                  style={{
                                    border:
                                      selectedEntry?.crm_audit_log_id ===
                                        entry.crm_audit_log_id
                                        ? "2px solid #6366f1"
                                        : "1px solid #e5e7eb",
                                    borderRadius: "8px",
                                    padding: "20px",
                                    marginBottom: "16px",
                                    backgroundColor:
                                      selectedEntry?.crm_audit_log_id ===
                                        entry.crm_audit_log_id
                                        ? "#f5f3ff"
                                        : "#fff",
                                    cursor:
                                      entry.action_type.key === "LOGIN" ||
                                        entry.action_type.key === "SIGNUP"
                                        ? "default"
                                        : "pointer",
                                    transition: "all 0.3s ease",
                                    position: "relative",
                                  }}
                                >
                                  {/* Badge in top-right corner */}
                                  {entry.action_type.key !== "LOGIN" &&
                                    entry.action_type.key !== "SIGNUP" && (
                                      <span
                                        style={{
                                          position: "absolute",
                                          top: "12px",
                                          right: "12px",
                                          padding: "4px 12px",
                                          borderRadius: "4px",
                                          fontSize: "10px",
                                          fontWeight: 500,
                                          cursor: "pointer",
                                        }}
                                      >
                                        <span className="text-primary text-underline">
                                          <i className="fa fa-info-circle" />{" "}
                                          See more infromation
                                        </span>
                                      </span>
                                    )}

                                  <div className="d-flex align-items-start justify-content-between">
                                    <div
                                      className="d-flex gap-3"
                                      style={{ paddingRight: "100px" }}
                                    >
                                      {entry.action_user && (
                                        <img
                                          src={
                                            entry.action_user.avatar_url ||
                                            `https://ui-avatars.com/api/?name=${entry.action_user.full_name}&background=6366f1&color=fff`
                                          }
                                          alt={entry.action_user.full_name}
                                          style={{
                                            width: "40px",
                                            height: "40px",
                                            borderRadius: "50%",
                                          }}
                                        />
                                      )}
                                      <div>
                                        <div className="mb-1">
                                          <span
                                            style={{
                                              fontWeight: 600,
                                              fontSize: "14px",
                                              color: "#374151",
                                            }}
                                          >
                                            {entry.action_user?.full_name ||
                                              "System"}
                                          </span>{" "}
                                          {entry.action_type.key === "LOGIN" ? (
                                            <>
                                              <span
                                                style={{
                                                  fontWeight: 600,
                                                  fontSize: "14px",
                                                  color: "#3b82f6",
                                                }}
                                              >
                                                Logged in
                                              </span>{" "}
                                              <span>to the portal</span>
                                            </>
                                          ) : (
                                            <>
                                              <span
                                                style={{
                                                  fontWeight: 600,
                                                  fontSize: "14px",
                                                  color:
                                                    entry.action_type.key ===
                                                      "CREATE"
                                                      ? "#10b981"
                                                      : entry.action_type
                                                        .key === "UPDATE"
                                                        ? "#6366f1"
                                                        : entry.action_type
                                                          .key === "DELETE"
                                                          ? "#ef4444"
                                                          : "#3b82f6",
                                                }}
                                              >
                                                {entry.action_type.display_name}
                                                d{" "}
                                              </span>
                                              <span
                                                style={{
                                                  fontWeight: 600,
                                                  fontSize: "14px",
                                                  color: "#374151",
                                                }}
                                              >
                                                {
                                                  entry.crm_related_entity
                                                    .display_name
                                                }
                                                {[
                                                  "Company",
                                                  "Contact",
                                                  "Lead",
                                                  "Pipeline",
                                                  "Deal",
                                                  "Proposal",
                                                  "Contract",
                                                  "Task",
                                                ].includes(
                                                  entry.crm_related_entity
                                                    .display_name
                                                ) && (
                                                    <>
                                                      {" - "}
                                                      <span>
                                                        {getEntityName(entry)}
                                                      </span>
                                                    </>
                                                  )}
                                              </span>
                                            </>
                                          )}
                                        </div>
                                        <p
                                          style={{
                                            margin: "8px 0",
                                            fontSize: "14px",
                                            color: "#6b7280",
                                          }}
                                        >
                                          {getDescription(entry)}
                                        </p>
                                        <div
                                          style={{
                                            fontSize: "13px",
                                            color: "#9ca3af",
                                          }}
                                        >
                                          {formatTimestamp(
                                            entry.created_datetime
                                          )}{" "}
                                          • {entry.action_source_type} • IP:{" "}
                                          {entry.ip_address}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}

                              {/* Pagination */}
                              {totalCount > pageSize && (
                                <div className="mt-4">
                                  <TablePagination
                                    currentPage={currentPage}
                                    totalItems={totalCount}
                                    filteredItems={totalCount}
                                    isFiltered={false}
                                    pageSize={pageSize}
                                    onPageChange={handlePageChange}
                                    onPageSizeChange={() => { }}
                                    entityName="entries"
                                  />
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    {selectedEntry && !isMobile && (
                      <div className="col-md-6">
                        <div className="audit-section">
                          <div className="d-flex align-items-center justify-content-between mb-4">
                            <h4
                              className="mb-0"
                              style={{ fontSize: "18px", fontWeight: 600 }}
                            >
                              Audit Log - Differences
                            </h4>
                            <div className="d-flex align-items-center gap-2">
                              <button
                                onClick={handleShowTimeline}
                                style={{
                                  background: "none",
                                  border: "none",
                                  color: "#6366f1",
                                  fontSize: "14px",
                                  fontWeight: 500,
                                  cursor: "pointer",
                                  textDecoration: "underline",
                                  padding: 0,
                                }}
                              >
                                Show Timeline
                              </button>
                              <button
                                className="btn btn-sm btn-icon"
                                onClick={() => setSelectedEntry(null)}
                                style={{
                                  border: "none",
                                  background: "transparent",
                                }}
                                title="Close differences view"
                              >
                                <i
                                  className="ti ti-x"
                                  style={{ fontSize: "20px", color: "#6b7280" }}
                                ></i>
                              </button>
                            </div>
                          </div>

                          <div
                            style={{
                              maxHeight: "600px",
                              overflowY: "auto",
                              paddingRight: "10px",
                            }}
                          >
                            {selectedEntry.old_value &&
                              selectedEntry.new_value ? (
                              (() => {
                                const fieldChanges = parseFieldChanges(
                                  selectedEntry.old_value,
                                  selectedEntry.new_value
                                );

                                return fieldChanges.length > 0 ? (
                                  <div
                                    style={{
                                      backgroundColor: "#f9fafb",
                                      border: "1px solid #e5e7eb",
                                      borderRadius: "8px",
                                      padding: "20px",
                                    }}
                                  >
                                    <div className="mb-3">
                                      <div
                                        style={{
                                          fontWeight: 600,
                                          fontSize: "15px",
                                          marginBottom: "8px",
                                        }}
                                      >
                                        Changes in{" "}
                                        {getRecordLabel(selectedEntry)}
                                      </div>
                                      <div
                                        style={{
                                          fontSize: "13px",
                                          color: "#6b7280",
                                        }}
                                      >
                                        <i
                                          className="ti ti-clock"
                                          style={{ marginRight: "6px" }}
                                        />
                                        {formatTimestamp(
                                          selectedEntry.created_datetime
                                        )}{" "}
                                        by{" "}
                                        {selectedEntry.action_user?.full_name ||
                                          "System"}
                                      </div>
                                    </div>

                                    {fieldChanges.map((change, idx) => (
                                      <div
                                        key={idx}
                                        className="mb-3"
                                        style={{
                                          borderBottom:
                                            idx < fieldChanges.length - 1
                                              ? "1px solid #e5e7eb"
                                              : "none",
                                          paddingBottom: "12px",
                                        }}
                                      >
                                        <div
                                          style={{
                                            fontWeight: 600,
                                            fontSize: "13px",
                                            marginBottom: "8px",
                                            color: "#374151",
                                          }}
                                        >
                                          {change.field}
                                        </div>
                                        <div className="row">
                                          <div className="col-6">
                                            <div
                                              style={{
                                                fontSize: "12px",
                                                fontWeight: 500,
                                                marginBottom: "4px",
                                                color: "#6b7280",
                                              }}
                                            >
                                              Previous Value
                                            </div>
                                            <div
                                              style={{
                                                fontSize: "13px",
                                                padding: "8px",
                                                backgroundColor: "#fef2f2",
                                                borderRadius: "4px",
                                                color: "#991b1b",
                                                wordBreak: "break-word",
                                              }}
                                            >
                                              {change.field ===
                                                "Preferred Language" &&
                                                change.oldValue
                                                ? getLanguageLabel(
                                                  change.oldValue
                                                )
                                                : formatFieldValue(
                                                  change.field,
                                                  change.oldValue
                                                )}
                                            </div>
                                          </div>
                                          <div className="col-6">
                                            <div
                                              style={{
                                                fontSize: "12px",
                                                fontWeight: 500,
                                                marginBottom: "4px",
                                                color: "#6b7280",
                                              }}
                                            >
                                              New Value
                                            </div>
                                            <div
                                              style={{
                                                fontSize: "13px",
                                                padding: "8px",
                                                backgroundColor: "#f0fdf4",
                                                borderRadius: "4px",
                                                color: "#166534",
                                                wordBreak: "break-word",
                                              }}
                                            >
                                              {change.field ===
                                                "Preferred Language" &&
                                                change.newValue
                                                ? getLanguageLabel(
                                                  change.newValue
                                                )
                                                : formatFieldValue(
                                                  change.field,
                                                  change.newValue
                                                )}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="text-center py-5">
                                    <i
                                      className="ti ti-info-circle"
                                      style={{
                                        fontSize: "48px",
                                        color: "#9ca3af",
                                      }}
                                    ></i>
                                    <p
                                      className="mt-3"
                                      style={{ color: "#6b7280" }}
                                    >
                                      No field changes detected.
                                    </p>
                                  </div>
                                );
                              })()
                            ) : (
                              <div className="text-center py-5">
                                <i
                                  className="ti ti-info-circle"
                                  style={{ fontSize: "48px", color: "#9ca3af" }}
                                ></i>
                                <p
                                  className="mt-3"
                                  style={{ color: "#6b7280" }}
                                >
                                  No differences available for this entry.
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Differences Modal (Mobile Only) */}
        <Modal
          show={showDiffModal}
          onHide={handleCloseDiffModal}
          size="lg"
          fullscreen="lg-down"
          centered
          dialogClassName="custom-modal-dialog"
          enforceFocus={false}
          restoreFocus={false}
          animation={false}
        >
          <Modal.Header style={{ paddingRight: "60px", position: "relative" }}>
            <Modal.Title style={{ fontSize: "18px", fontWeight: 600 }}>
              Audit Log - Differences
            </Modal.Title>
            <button
              type="button"
              onClick={handleCloseDiffModal}
              aria-label="Close"
              className="modal-close-btn"
              style={{
                position: "absolute",
                right: "20px",
                top: "20px",
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                border: "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "20px",
                zIndex: 1050,
                transition: "all 0.2s",
              }}
            >
              <i className="ti ti-x" />
            </button>
          </Modal.Header>
          <Modal.Body
            style={{
              maxHeight: "600px",
              overflowY: "scroll",
            }}
          >
            {selectedEntry &&
              selectedEntry.old_value &&
              selectedEntry.new_value ? (
              (() => {
                const fieldChanges = parseFieldChanges(
                  selectedEntry.old_value,
                  selectedEntry.new_value
                );

                return fieldChanges.length > 0 ? (
                  <div
                    style={{
                      backgroundColor: "#f9fafb",
                      border: "1px solid #e5e7eb",
                      borderRadius: "8px",
                      padding: "20px",
                    }}
                  >
                    <div className="mb-3">
                      <div
                        style={{
                          fontWeight: 600,
                          fontSize: "15px",
                          marginBottom: "8px",
                        }}
                      >
                        Changes in {getEntityName(selectedEntry)}
                      </div>
                      <div
                        style={{
                          fontSize: "13px",
                          color: "#6b7280",
                        }}
                      >
                        <i
                          className="ti ti-clock"
                          style={{ marginRight: "6px" }}
                        />
                        {formatTimestamp(selectedEntry.created_datetime)} by{" "}
                        {selectedEntry.action_user?.full_name || "System"}
                      </div>
                    </div>

                    {fieldChanges.map((change, idx) => (
                      <div
                        key={idx}
                        className="mb-3"
                        style={{
                          borderBottom:
                            idx < fieldChanges.length - 1
                              ? "1px solid #e5e7eb"
                              : "none",
                          paddingBottom: "12px",
                        }}
                      >
                        <div
                          style={{
                            fontWeight: 600,
                            fontSize: "13px",
                            marginBottom: "8px",
                            color: "#374151",
                          }}
                        >
                          {change.field}
                        </div>
                        <div className="row">
                          <div className="col-6">
                            <div
                              style={{
                                fontSize: "12px",
                                fontWeight: 500,
                                marginBottom: "4px",
                                color: "#6b7280",
                              }}
                            >
                              Previous Value
                            </div>
                            <div
                              style={{
                                fontSize: "13px",
                                padding: "8px",
                                backgroundColor: "#fef2f2",
                                borderRadius: "4px",
                                color: "#991b1b",
                                wordBreak: "break-word",
                              }}
                            >
                              {change.field === "Preferred Language" &&
                                change.oldValue
                                ? getLanguageLabel(change.oldValue)
                                : formatFieldValue(
                                  change.field,
                                  change.oldValue
                                )}
                            </div>
                          </div>
                          <div className="col-6">
                            <div
                              style={{
                                fontSize: "12px",
                                fontWeight: 500,
                                marginBottom: "4px",
                                color: "#6b7280",
                              }}
                            >
                              New Value
                            </div>
                            <div
                              style={{
                                fontSize: "13px",
                                padding: "8px",
                                backgroundColor: "#f0fdf4",
                                borderRadius: "4px",
                                color: "#166534",
                                wordBreak: "break-word",
                              }}
                            >
                              {change.field === "Preferred Language" &&
                                change.newValue
                                ? getLanguageLabel(change.newValue)
                                : formatFieldValue(
                                  change.field,
                                  change.newValue
                                )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-5">
                    <i
                      className="ti ti-info-circle"
                      style={{ fontSize: "48px", color: "#9ca3af" }}
                    ></i>
                    <p className="mt-3" style={{ color: "#6b7280" }}>
                      No field changes detected.
                    </p>
                  </div>
                );
              })()
            ) : selectedEntry ? (
              <div className="text-center py-5">
                <i
                  className="ti ti-info-circle"
                  style={{ fontSize: "48px", color: "#9ca3af" }}
                ></i>
                <p className="mt-3" style={{ color: "#6b7280" }}>
                  No differences available for this entry.
                </p>
              </div>
            ) : null}
          </Modal.Body>
        </Modal>

        {/* Timeline Modal */}
        <Modal
          show={showTimelineModal}
          onHide={handleCloseTimelineModal}
          size="lg"
          fullscreen="lg-down"
          centered
          dialogClassName="custom-modal-dialog"
          enforceFocus={false}
          restoreFocus={false}
          animation={false}
        >
          <Modal.Header style={{ paddingRight: "60px", position: "relative" }}>
            <Modal.Title style={{ fontSize: "18px", fontWeight: 600 }}>
              Audit Log - Visual Timeline
            </Modal.Title>
            <button
              type="button"
              onClick={handleCloseTimelineModal}
              aria-label="Close"
              className="modal-close-btn"
              style={{
                position: "absolute",
                right: "20px",
                top: "20px",
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                border: "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "20px",
                zIndex: 1050,
                transition: "all 0.2s",
              }}
            >
              <i className="ti ti-x" />
            </button>
          </Modal.Header>
          <Modal.Body
            style={{
              maxHeight: "600px",
              overflowY: "scroll",
            }}
          >
            {loadingTimeline ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3" style={{ color: "#6b7280" }}>
                  Loading timeline...
                </p>
              </div>
            ) : timelineData ? (
              <div
                style={{
                  position: "relative",
                  paddingLeft: "40px",
                }}
                className="mt-3"
              >
                {/* Combine all timeline entries and sort by date */}
                {[
                  ...timelineData.create.map((e) => ({ ...e, type: "CREATE" })),
                  ...timelineData.update.map((e) => ({ ...e, type: "UPDATE" })),
                  ...timelineData.delete.map((e) => ({ ...e, type: "DELETE" })),
                ]
                  .sort(
                    (a, b) =>
                      new Date(a.created_datetime).getTime() -
                      new Date(b.created_datetime).getTime()
                  )
                  .map((entry, index, allEntries) => {
                    const fieldChanges =
                      entry.old_value && entry.new_value
                        ? parseFieldChanges(entry.old_value, entry.new_value)
                        : [];

                    return (
                      <div
                        key={entry.crm_audit_log_id}
                        style={{
                          position: "relative",
                          marginBottom:
                            index === allEntries.length - 1 ? 0 : "40px",
                        }}
                      >
                        {/* Connecting line */}
                        {index < allEntries.length - 1 && (
                          <div
                            style={{
                              position: "absolute",
                              left: "-20px",
                              top: "40px",
                              width: "2px",
                              height: "calc(100% + 40px)",
                              backgroundColor:
                                entry.type === "UPDATE"
                                  ? "#8b5cf6"
                                  : entry.type === "CREATE"
                                    ? "#10b981"
                                    : "#ef4444",
                            }}
                          />
                        )}

                        {/* Timeline icon */}
                        <div
                          style={{
                            position: "absolute",
                            left: "-30px",
                            top: "10px",
                            width: "24px",
                            height: "24px",
                            borderRadius: "50%",
                            backgroundColor:
                              entry.type === "UPDATE"
                                ? "#8b5cf6"
                                : entry.type === "CREATE"
                                  ? "#10b981"
                                  : "#ef4444",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            border: "3px solid #fff",
                            boxShadow: "0 0 0 2px #f3f4f6",
                          }}
                        >
                          {entry.type === "UPDATE" && (
                            <i
                              className="fa-solid fa-pencil"
                              style={{
                                fontSize: "12px",
                                color: "#fff",
                              }}
                            />
                          )}
                          {entry.type === "CREATE" && (
                            <i
                              className="fa-solid fa-plus"
                              style={{
                                fontSize: "12px",
                                color: "#fff",
                              }}
                            />
                          )}
                          {entry.type === "DELETE" && (
                            <i
                              className="ti ti-trash"
                              style={{
                                fontSize: "12px",
                                color: "#fff",
                              }}
                            />
                          )}
                        </div>

                        {/* Timeline content */}
                        <div>
                          <div className="mb-3">
                            <div
                              style={{
                                fontWeight: 600,
                                fontSize: "14px",
                                marginBottom: "8px",
                              }}
                            >
                              {entry.action_type.display_name}d by{" "}
                              {entry.action_user?.full_name || "System"}
                            </div>
                            <div
                              style={{
                                fontSize: "13px",
                                color: "#6b7280",
                              }}
                            >
                              <i
                                className="ti ti-clock"
                                style={{ marginRight: "6px" }}
                              />
                              {formatTimestamp(entry.created_datetime)} •{" "}
                              {entry.action_source_type}
                            </div>
                          </div>

                          {/* Show field changes for UPDATE entries */}
                          {entry.type === "UPDATE" &&
                            fieldChanges.length > 0 && (
                              <div
                                style={{
                                  backgroundColor: "#f9fafb",
                                  border: "1px solid #e5e7eb",
                                  borderRadius: "8px",
                                  padding: "16px",
                                }}
                              >
                                {fieldChanges.map((change, idx) => (
                                  <div
                                    key={idx}
                                    className="mb-3"
                                    style={{
                                      borderBottom:
                                        idx < fieldChanges.length - 1
                                          ? "1px solid #e5e7eb"
                                          : "none",
                                      paddingBottom: "12px",
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontWeight: 600,
                                        fontSize: "12px",
                                        marginBottom: "8px",
                                        color: "#374151",
                                      }}
                                    >
                                      {change.field}
                                    </div>
                                    <div className="row">
                                      <div className="col-6">
                                        <div
                                          style={{
                                            fontSize: "11px",
                                            fontWeight: 500,
                                            marginBottom: "4px",
                                            color: "#6b7280",
                                          }}
                                        >
                                          Previous
                                        </div>
                                        <div
                                          style={{
                                            fontSize: "12px",
                                            padding: "6px",
                                            backgroundColor: "#fef2f2",
                                            borderRadius: "4px",
                                            color: "#991b1b",
                                            wordBreak: "break-word",
                                          }}
                                        >
                                          {change.field ===
                                            "Preferred Language" &&
                                            change.oldValue
                                            ? getLanguageLabel(change.oldValue)
                                            : formatFieldValue(
                                              change.field,
                                              change.oldValue
                                            )}
                                        </div>
                                      </div>
                                      <div className="col-6">
                                        <div
                                          style={{
                                            fontSize: "11px",
                                            fontWeight: 500,
                                            marginBottom: "4px",
                                            color: "#6b7280",
                                          }}
                                        >
                                          New
                                        </div>
                                        <div
                                          style={{
                                            fontSize: "12px",
                                            padding: "6px",
                                            backgroundColor: "#f0fdf4",
                                            borderRadius: "4px",
                                            color: "#166534",
                                            wordBreak: "break-word",
                                          }}
                                        >
                                          {change.field ===
                                            "Preferred Language" &&
                                            change.newValue
                                            ? getLanguageLabel(change.newValue)
                                            : formatFieldValue(
                                              change.field,
                                              change.newValue
                                            )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                        </div>
                      </div>
                    );
                  })}
              </div>
            ) : (
              <div className="text-center py-5">
                <i
                  className="ti ti-info-circle"
                  style={{ fontSize: "48px", color: "#9ca3af" }}
                ></i>
                <p className="mt-3" style={{ color: "#6b7280" }}>
                  No timeline data available.
                </p>
              </div>
            )}
          </Modal.Body>
        </Modal>
      </div>
    </>
  );
};

export default AuditLog;
